import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Nullable } from 'v5/platform/typescript';
import { WithServices } from 'v5/platform/ts/services';
import {
  type ToastService,
  WithToastService
} from 'v5/services/front/ToastService/ToastService';

/** Event emitted by `@plugin-web-update-notification/vite` when a new build is available. */
const APP_WEB_UPDATE_NOTICE_EVENT = 'plugin_web_update_notice';

export interface VersionUpdateNotifierProps
  extends WithServices<[WithToastService]> {}

/**
 * Subscribes to client-side app update events and shows a refresh toast.
 * Renders nothing.
 */
export function VersionUpdateNotifier({
  services
}: VersionUpdateNotifierProps) {
  const { t } = useTranslation('core');
  const { toastService } = services;

  useEffect(() => {
    if (!import.meta.env.PROD) {
      return;
    }

    let activeUpdateToast: Nullable<ReturnType<ToastService['open']>> = null;

    const clearActiveToastState = () => {
      activeUpdateToast = null;
      window.pluginWebUpdateNotice_?.dismissUpdate();
    };

    const onUpdate = () => {
      if (activeUpdateToast) {
        return;
      }

      activeUpdateToast = toastService.open({
        type: 'info',
        autoClose: false,
        message: t('app_update.message'),
        action: {
          label: <span className="text-base">{t('app_update.refresh')}</span>,
          onClick: () => {
            window.location.reload();
          }
        },
        onClose: clearActiveToastState,
        onCloseAll: clearActiveToastState
      });
    };

    document.body.addEventListener(APP_WEB_UPDATE_NOTICE_EVENT, onUpdate);

    return () => {
      document.body.removeEventListener(APP_WEB_UPDATE_NOTICE_EVENT, onUpdate);
      activeUpdateToast?.dismiss();
      activeUpdateToast = null;
    };
  }, [toastService, t]);

  return null;
}
